import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  booleanAttribute,
  forwardRef,
  ViewChild,
  AfterViewInit
} from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ConnectedPosition, CdkConnectedOverlay } from '@angular/cdk/overlay';
import type { KeyValueSelect } from '@shared/interfaces/key-value-select.interface';

type SelectItem = KeyValueSelect & { display?: string | null };

type PrimitiveKey = string | number | boolean;

@Component({
  selector: 'app-so-select-virtual-scroll',
  templateUrl: './so-select-virtual-scroll.component.html',
  styleUrls: ['./so-select-virtual-scroll.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SoSelectVirtualScrollComponent),
      multi: true
    }
  ],
  animations: [
    trigger('panelAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scaleY(0.95)', transformOrigin: 'top center' }),
        animate('160ms cubic-bezier(0.2, 0, 0, 1)', style({ opacity: 1, transform: 'scaleY(1)' }))
      ]),
      transition(':leave', [
        animate('140ms ease-in', style({ opacity: 0, transform: 'scaleY(0.98)' }))
      ])
    ])
  ]
})
export class SoSelectVirtualScrollComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @Output() selectionChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() selectionText: EventEmitter<any> = new EventEmitter<any>();

  @Input() selectLabel: string = '';
  @Input() selectedItemId: PrimitiveKey | null = null;
  @Input() isLoading: boolean = false;
  @Input() customStyle: string = '';
  @Input() displayValueStyle = '';
  @Input() displayValueClass = '';
  @Input() name: string = '';
  @Input() labelStyle: string = '';
  @Input({ transform: booleanAttribute }) isWhiteBox: boolean = false;
  @Input({ transform: booleanAttribute }) requiredMark: boolean = false;
  @Input() disabledKeys: Array<PrimitiveKey | string | number | null> = [];
  @Input() isDisabled: boolean = false;
  @Input() isInitiallyEmpty: boolean = false;
  @Input() tooltipDisabledText: string | null = '';
  @Input() formControl: FormControl | null = null;
  @Input() display: 'vertical' | 'horizontal' = 'vertical';
  @Input() addSelectOption: boolean = false;
  @Input() lang: string = '';
  @Input() itemSize = 36;
  @Input() panelMaxHeight = 240;

  @ViewChild(CdkVirtualScrollViewport) viewport?: CdkVirtualScrollViewport;
  @ViewChild(CdkConnectedOverlay) connectedOverlay?: CdkConnectedOverlay;
  @ViewChild('triggerRef') triggerRef?: ElementRef<HTMLElement>;

  readonly maxVisibleItems = 6;
  private _items: KeyValueSelect[] = [];
  private displayMap: Map<string, string | null> = new Map();
  private isComponentDestroyed = false;

  value: PrimitiveKey | null = null;
  onChange: (value: PrimitiveKey | null) => void = () => {};
  onTouched: () => void = () => {};

  public currentDisplayItem: string | null = null;
  public isOpen = false;
  public activeIndex = -1;
  public panelId = `virtual-select-panel-${Math.random().toString(36).slice(2)}`;
  public triggerWidth = 0;
  public overlayPositions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 4
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetY: -4
    }
  ];

  get items(): KeyValueSelect[] {
    return this._items;
  }

  @Input({ required: true })
  set items(value: SelectItem[] | undefined | null) {
    this.displayMap.clear();
    const newItems = (value ?? []).map((element, index) => {
      const key = element?.key ?? index;
      const optionValue = element?.value ?? '';
      const display = element?.display ?? optionValue ?? null;
      const normalizedKey = this.normalizeKey(key);
      if (normalizedKey !== null) {
        this.displayMap.set(normalizedKey, display);
      }
      return { key, value: optionValue };
    });

    if (this.addSelectOption && newItems.length > 0) {
      newItems.unshift({ key: '', value: 'Seleccionar' });
      this.displayMap.set('', 'Seleccionar');
    }

    this._items = newItems;
    this.syncCurrentDisplay();
    this.ensureActiveIndex();
  }

  constructor() {}

  ngOnInit(): void {
    this.syncCurrentDisplay();
  }

  ngAfterViewInit(): void {
    this.setTriggerWidth();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.keys(changes).includes('items')) {
      const currentValue: SelectItem[] | undefined = changes['items']?.currentValue;
      if (this.isInitiallyEmpty && currentValue && !currentValue.some(item => this.normalizeKey(item.key) === this.normalizeKey(this.selectedItemId))) {
        this.selectedItemId = null;
        this.value = null;
      }
    }

    if (changes['selectedItemId'] && !changes['selectedItemId'].firstChange) {
      this.value = this.selectedItemId;
      this.syncCurrentDisplay();
      this.ensureActiveIndex();
    }

    if (changes['disabledKeys'] && !changes['disabledKeys'].firstChange) {
      this.ensureActiveIndex();
    }
  }

  ngOnDestroy(): void {
    this.isComponentDestroyed = true;
    this.displayMap.clear();
  }

  get touched(): boolean {
    return this.formControl?.touched ?? false;
  }

  get valid(): boolean {
    return this.formControl?.valid ?? true;
  }

  writeValue(value: PrimitiveKey | null): void {
    this.value = value;
    this.selectedItemId = value;
    this.syncCurrentDisplay();
    this.ensureActiveIndex();
  }

  registerOnChange(fn: (value: PrimitiveKey | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  shouldBeDisabled(key: PrimitiveKey | string | number | null): boolean {
    return this.disabledKeys.some(currentKey => this.normalizeKey(currentKey) === this.normalizeKey(key));
  }

  onTriggerClick(): void {
    if (this.isDisabled) return;
    this.isOpen ? this.closePanel() : this.openPanel();
  }

  onTriggerKeydown(event: KeyboardEvent): void {
    if (this.isDisabled) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!this.isOpen) {
          this.openPanel();
        }
        this.moveActiveIndex(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!this.isOpen) {
          this.openPanel();
        }
        this.moveActiveIndex(-1);
        break;
      case 'Enter':
      case ' ': {
        event.preventDefault();
        if (!this.isOpen) {
          this.openPanel();
        } else if (this.activeIndex >= 0 && this.activeIndex < this.items.length) {
          const item = this.items[this.activeIndex];
          this.selectItem(item);
        }
        break;
      }
      case 'Space':
        event.preventDefault();
        if (!this.isOpen) {
          this.openPanel();
        } else if (this.activeIndex >= 0 && this.activeIndex < this.items.length) {
          const item = this.items[this.activeIndex];
          this.selectItem(item);
        }
        break;
      case 'Escape':
        if (this.isOpen) {
          event.preventDefault();
          this.closePanel();
        }
        break;
      default:
        break;
    }
  }

  trackByKey(_index: number, item: KeyValueSelect): PrimitiveKey {
    return item.key;
  }

  getOptionId(index: number): string {
    return `${this.panelId}-option-${index}`;
  }

  selectItem(item: KeyValueSelect): void {
    if (this.shouldBeDisabled(item.key)) {
      return;
    }

    this.value = item.key as PrimitiveKey;
    this.selectedItemId = item.key as PrimitiveKey;
    const normalizedKey = this.normalizeKey(item.key);
    this.currentDisplayItem = normalizedKey !== null ? this.displayMap.get(normalizedKey) ?? item.value ?? null : item.value ?? null;
    this.onChange(this.value);
    this.onTouched();
    this.selectionChange.emit(this.value);
    const emittedText = normalizedKey !== null ? this.displayMap.get(normalizedKey) ?? item.value : item.value;
    this.selectionText.emit(emittedText);
    this.closePanel();
  }

  get activeOptionId(): string | null {
    return this.activeIndex >= 0 ? this.getOptionId(this.activeIndex) : null;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (!this.isOpen) {
      return;
    }
    this.setTriggerWidth();
    this.connectedOverlay?.overlayRef?.updatePosition();
  }

  openPanel(): void {
    if (this.isOpen || this.isDisabled) return;
    this.isOpen = true;
    this.setTriggerWidth();
    this.ensureActiveIndex();
    queueMicrotask(() => {
      if (this.isComponentDestroyed) return;
      this.connectedOverlay?.overlayRef?.updatePosition();
    });
  }

  closePanel(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
  }

  private moveActiveIndex(delta: number): void {
    if (!this.items.length) return;

    const firstEnabled = this.findFirstEnabledIndex();
    if (firstEnabled === -1) {
      this.activeIndex = -1;
      return;
    }

    let nextIndex = this.activeIndex;
    const maxAttempts = this.items.length;
    let attempts = 0;

    do {
      attempts++;
      nextIndex = nextIndex + delta;
      if (nextIndex < 0) nextIndex = this.items.length - 1;
      if (nextIndex >= this.items.length) nextIndex = 0;
    } while (attempts <= maxAttempts && this.shouldBeDisabled(this.items[nextIndex].key));

    if (this.shouldBeDisabled(this.items[nextIndex].key)) {
      this.activeIndex = -1;
      return;
    }

    this.activeIndex = nextIndex;
    this.scrollToActiveIndex();
  }

  private ensureActiveIndex(): void {
    const normalizedValue = this.normalizeKey(this.value ?? this.selectedItemId ?? null);
    const fallbackIndex = this.findFirstEnabledIndex();

    if (normalizedValue === null) {
      this.activeIndex = -1;
    } else {
      const foundIndex = this.items.findIndex(item => this.normalizeKey(item.key) === normalizedValue);
      if (foundIndex !== -1 && !this.shouldBeDisabled(this.items[foundIndex].key)) {
        this.activeIndex = foundIndex;
      } else {
        this.activeIndex = fallbackIndex;
      }
    }
    this.scrollToActiveIndex();
  }

  private findFirstEnabledIndex(): number {
    const index = this.items.findIndex(item => !this.shouldBeDisabled(item.key as PrimitiveKey));
    return index;
  }

  private scrollToActiveIndex(): void {
    if (!this.isOpen || this.activeIndex == null || this.activeIndex < 0) return;

    queueMicrotask(() => {
      if (this.isComponentDestroyed) return;
      const viewport = this.viewport;
      if (viewport) {
        viewport.scrollToIndex(this.activeIndex, 'smooth');
      }
    });
  }

  private syncCurrentDisplay(): void {
    const normalizedValue = this.normalizeKey(this.value ?? this.selectedItemId ?? null);
    if (normalizedValue === null) {
      this.currentDisplayItem = null;
      return;
    }
    this.currentDisplayItem = this.displayMap.get(normalizedValue) ?? null;
  }

  normalizeKey(key: PrimitiveKey | string | number | null | undefined): string | null {
    if (key === null || key === undefined) {
      return null;
    }
    return String(key);
  }

  private setTriggerWidth(): void {
    this.triggerWidth = this.triggerRef?.nativeElement.getBoundingClientRect().width ?? 0;
  }

  get panelHeight(): number {
    const totalItems = this.items.length;
    if (totalItems === 0) return Math.min(this.itemSize, this.panelMaxHeight);
    const visibleItems = Math.min(totalItems, this.maxVisibleItems);
    const desiredHeight = visibleItems * this.itemSize;
    return Math.min(desiredHeight, this.panelMaxHeight);
  }

  get viewportOverflowY(): 'auto' | 'hidden' {
    const totalContentHeight = this.items.length * this.itemSize;
    return totalContentHeight > this.panelHeight ? 'auto' : 'hidden';
  }
}
